// AIDEV-NOTE: Feature flags are now included in user details to reduce API calls
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "~/hooks/use-api-client";
import { useUserDetails } from "~/hooks/queries/use-user-details";
import { captureException } from "~/lib/analytics";
import { logger } from "~/lib/logger";

// AIDEV-NOTE: Feature flags use kebab-case keys from the DB.
interface FeatureFlags {
    [key: string]: boolean | undefined;
    "enable-recall"?: boolean;
    "enable-meditation"?: boolean;
    "show-session-secondary-sidebar"?: boolean;
    "show-cost-transparency"?: boolean;
    "enable-voice-clone"?: boolean;
    "enable-unaddressed-parts-analysis"?: boolean;
    "show-onboarding-checklist"?: boolean;
    "show-client-context"?: boolean;
    "enable-dpmp"?: boolean;
    "show-dpmp-offer"?: boolean;
    "enable-consultant-access"?: boolean;
    "enable-consultant-data-minimization"?: boolean;
    "enable-recording-chunk-recovery"?: boolean;
    "enable-recording-split"?: boolean;
}

// AIDEV-NOTE: Gates the Client Context ENTRY POINTS (sidebar item, mobile action,
// consultation modal button, post-add-client toast action). The notes route itself
// stays reachable by direct URL. Fail-closed — see modules.server/feature-flags/fail-modes.ts.
export const SHOW_CLIENT_CONTEXT_FLAG = "show-client-context";

// AIDEV-NOTE: DPMP® (Daily Parts Meditation Practice) package. ENABLE_DPMP_FLAG is the
// user's own opt-in state (self-serve via Settings → Add-Ons / the /parts offer modal);
// it gates the flip part cards' PCC content, part-detail healing steps, DPMP resource
// banners, and Michelle Glass's meditation voice. SHOW_DPMP_OFFER_FLAG is the
// admin-controlled rollout of the OFFER surfaces. Both fail closed.
export const ENABLE_DPMP_FLAG = "enable-dpmp";
export const SHOW_DPMP_OFFER_FLAG = "show-dpmp-offer";
// AIDEV-NOTE: Consultant-access flag key lives with its feature —
// see ~/features/consultant-access/constants.ts (fail-closed).
export { ENABLE_CONSULTANT_ACCESS_FLAG } from "~/features/consultant-access/constants";

// AIDEV-NOTE: Orphaned-chunk rescue flag lives with its feature —
// see ~/features/recording-recovery/constants.ts (fail-closed).
export { ENABLE_RECORDING_CHUNK_RECOVERY_FLAG } from "~/features/recording-recovery/constants";

// AIDEV-NOTE: Recording-splitter flag key lives with its feature —
// see ~/features/session-splitter/types.ts (fail-closed, training cohorts only).
export { RECORDING_SPLIT_FLAG_KEY } from "~/features/session-splitter/types";

export function useFeatureFlag(key: string): boolean {
    const { data: userDetails } = useUserDetails();

    // AIDEV-NOTE: Unauthenticated users and missing loader data resolve flags as off.
    return userDetails?.featureFlags?.[key] === true;
}

export const useAccountFeatureFlags = () => {
    const { data: userDetails, isLoading, error } = useUserDetails();

    const featureFlags = (userDetails?.featureFlags ?? {}) as FeatureFlags;

    return {
        data: featureFlags,
        isLoading,
        isError: !!error,
        error,
    };
};

export const useUpdateAccountFeatureFlags = () => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (featureFlags: FeatureFlags) => {
            const response = await apiClient.put(
                "/account/feature-flags",
                featureFlags as Record<string, unknown>,
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(
                    error.error || "Failed to update feature flags",
                );
            }

            const result = await response.json();
            return result;
        },
        onSuccess: () => {
            // AIDEV-NOTE: Invalidate user details query since feature flags are now part of it
            queryClient.invalidateQueries({
                queryKey: ["user"],
            });
        },
        onError: (error) => {
            logger
                .withError(error as Error)
                .error("Failed to update feature flags");
            captureException("Error updating account feature flags", error);
        },
    });
};
